import React from 'react';
import useIsMobile from './hooks/useIsMobile';

const Support = ({ onBack }) => {
  const isMobile = useIsMobile();
  return (
    <div style={{
      minHeight: '100vh',
      background: '#0B0B10',
      color: '#FFFFFF',
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0B0B10 0%, #16161D 100%)',
        borderBottom: '1px solid rgba(255, 138, 61, 0.2)',
        padding: '1rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#FF8A3D',
            fontSize: '1.5rem',
            cursor: 'pointer',
            padding: '0.25rem'
          }}
        >
          ←
        </button>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>
          Support
        </h1>
      </div>

      {/* Content */}
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: isMobile ? '1.5rem 1rem' : '2rem 1.5rem',
        lineHeight: '1.8',
      }}>
        {/* Contact Card */}
        <div style={{
          background: '#16161D',
          borderRadius: '12px',
          padding: '2rem',
          border: '1px solid rgba(255, 138, 61, 0.2)',
          textAlign: 'center',
          marginBottom: '2rem',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💬</div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>
            Need Help?
          </h2>
          <p style={{ color: '#8b9cb3', marginBottom: '1.5rem' }}>
            We're here to help with any questions, issues, or feedback about Runrora.
          </p>
          <a
            href="mailto:support@runrora.com"
            style={{
              display: 'inline-block',
              background: '#FF8A3D',
              color: '#0B0B10',
              padding: '0.75rem 2rem',
              borderRadius: '8px',
              fontWeight: 600,
              textDecoration: 'none',
              fontSize: '1rem',
            }}
          >
            Email Us: support@runrora.com
          </a>
        </div>

        {/* How It Works */}
        <h3 style={{ fontSize: '1.15rem', fontWeight: 600, color: '#FF8A3D', marginBottom: '1rem' }}>
          How It Works
        </h3>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          marginBottom: '2rem',
        }}>
          {[
            { step: 1, icon: '🔍', title: 'Discover Races', desc: 'Browse 125+ races — marathons, halfs, 10Ks, triathlons, and ultras' },
            { step: 2, icon: '✅', title: 'Register & Plan', desc: 'Register for a race and build your game plan with pace splits, nutrition, travel, and checklists' },
            { step: 3, icon: '👥', title: 'Join Communities', desc: 'Connect with runners in race and city communities. Share tips on message boards' },
            { step: 4, icon: '🏁', title: 'Race Day & Reflect', desc: 'Plan your race morning timeline, then journal your performance after you finish' },
          ].map(({ step, icon, title, desc }) => (
            <div key={step} style={{
              flex: isMobile ? '1 1 100%' : '1 1 calc(50% - 0.5rem)',
              minWidth: isMobile ? 'auto' : '200px',
              background: '#16161D',
              border: '1px solid rgba(255, 138, 61, 0.15)',
              borderRadius: '12px',
              padding: '1.25rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>{icon}</span>
                <div>
                  <span style={{ color: '#FF8A3D', fontSize: '0.75rem', fontWeight: 700 }}>STEP {step}</span>
                  <div style={{ fontWeight: 600, fontSize: '1rem' }}>{title}</div>
                </div>
              </div>
              <p style={{ color: '#8b9cb3', fontSize: '0.9rem', margin: 0, lineHeight: 1.5 }}>{desc}</p>
            </div>
          ))}
        </div>

        {/* Features */}
        <h3 style={{ fontSize: '1.15rem', fontWeight: 600, color: '#FF8A3D', marginBottom: '1rem' }}>
          Features
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '0.75rem',
          marginBottom: '2rem',
        }}>
          {[
            { icon: '⏱️', title: 'Pace Calculator', desc: 'Set your goal finish time and get mile-by-mile splits with even, negative, or positive pacing strategies' },
            { icon: '🌅', title: 'Race Day Timeline', desc: 'Plan your race morning from wake-up to gun time. Auto-generate a suggested schedule or customize your own' },
            { icon: '🍊', title: 'Nutrition Planner', desc: 'Track your race fuel — gels, electrolytes, hydration. Browse brands or add homemade recipes' },
            { icon: '📋', title: 'Packing Checklist', desc: 'Categorized gear list by type — Gear, Admin, Body Care, Fuel. Never forget your race bib again' },
            { icon: '✈️', title: 'Travel Plans', desc: 'Organize flights, hotels, and car rentals with confirmation numbers. Paste a booking and we\'ll extract the details' },
            { icon: '📝', title: 'Race Reflection', desc: 'After race day, log your finish time, rate your experience, and journal what worked and what didn\'t' },
            { icon: '👥', title: 'Communities', desc: 'Join race and city running communities. Discuss training, course tips, and meetups on message boards' },
            { icon: '🏃', title: 'Athlete Profiles', desc: 'Follow runners, see who\'s racing the same events, and build your running network' },
          ].map(({ icon, title, desc }) => (
            <div key={title} style={{
              background: '#16161D',
              border: '1px solid rgba(139, 156, 179, 0.15)',
              borderRadius: '10px',
              padding: '1.25rem',
              display: 'flex',
              gap: '0.75rem',
              alignItems: 'flex-start',
            }}>
              <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>{icon}</span>
              <div>
                <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{title}</div>
                <div style={{ color: '#8b9cb3', fontSize: '0.9rem', lineHeight: 1.5 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <h3 style={{ fontSize: '1.15rem', fontWeight: 600, color: '#FF8A3D', marginBottom: '1rem' }}>
          Frequently Asked Questions
        </h3>

        <FAQItem
          question="How do I create an account?"
          answer="You can sign up with your email and password, or use Sign in with Apple for a quick setup."
        />
        <FAQItem
          question="How do I join a race community?"
          answer="Browse races in the Explore tab and register for a race. You'll automatically be added to that race's community where you can connect with other participants."
        />
        <FAQItem
          question="What is Race-Ready Mode?"
          answer="Race-Ready Mode is a focus feature that centers your app experience around race preparation. Enable it in Settings to jump straight to your race dashboard with strategy, checklists, and travel planning."
        />
        <FAQItem
          question="How do I follow other athletes?"
          answer="Visit another athlete's profile and tap the Follow button. Their activity will then appear in your Feed."
        />
        <FAQItem
          question="What is the Pace Calculator?"
          answer="Set your goal finish time, pick a race distance, and choose a pacing strategy (even, negative, or positive split). We'll calculate your mile-by-mile target splits so you know exactly what pace to hit."
        />
        <FAQItem
          question="How does the Race Day Timeline work?"
          answer="Enter your race gun time and tap 'Start with Suggested Timeline' to auto-generate a morning schedule — from wake-up to the starting line. You can add, edit, or remove events to customize it."
        />
        <FAQItem
          question="How do I track my nutrition plan?"
          answer="Use the Nutrition tab in your race detail to add fuel items by brand from our directory or create your own homemade recipes. Set timing and notes for each item."
        />
        <FAQItem
          question="What happens after my race?"
          answer="The Reflection tab unlocks after race day. Log your actual finish time, rate the experience with 1-5 stars, and journal what worked, what didn't, and what you'd change next time."
        />
        <FAQItem
          question="How do I delete my account?"
          answer="Contact us at support@runrora.com and we'll process your account deletion within 30 days."
        />
        <FAQItem
          question="I found a bug or have a feature request"
          answer="We'd love to hear from you! Email us at support@runrora.com with details and we'll look into it."
        />
      </div>
    </div>
  );
};

const FAQItem = ({ question, answer }) => (
  <div style={{
    background: '#16161D',
    borderRadius: '10px',
    padding: '1.25rem',
    marginBottom: '0.75rem',
    border: '1px solid rgba(139, 156, 179, 0.15)',
  }}>
    <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{question}</div>
    <div style={{ color: '#8b9cb3', fontSize: '0.95rem' }}>{answer}</div>
  </div>
);

export default Support;
